import { useMemo, useState } from 'react';
import { App, Badge, Button, Card, Input, Modal, Popover, Rate, Select, Space, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ChatCircleText, FunnelSimple, PaperPlaneRight, PencilSimple } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { DataTable } from '../../components/DataTable';
import { ErrorState } from '../../components/PageStates';
import { StatusTag } from '../../components/StatusTag';
import { ApiError, NetworkError } from '../../api/client';
import { isSuperAdmin, useCurrentStaff } from '../../auth/session';
import { catalogApi, catalogKeys } from '../catalog/api';
import { tokens } from '../../theme';
import { VND } from '../../format';
import {
  reviewApi,
  reviewKeys,
  type AdminMealReview,
  type MealReviewFilters,
  type MealReviewState,
} from './api';

const REPLY_MAX = 2000;

/** Trạng thái → nhãn + tone chip (cam không mang nghĩa trạng thái — ADR-009). */
const STATE_META: Record<MealReviewState, { label: string; color: string }> = {
  New: { label: 'Mới', color: 'processing' },
  Seen: { label: 'Đã xem', color: 'default' },
  Replied: { label: 'Đã trả lời', color: 'success' },
};

function formatMoment(iso: string): string {
  return new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Ho_Chi_Minh',
  }).format(new Date(iso));
}

export function ReviewsPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const staff = useCurrentStaff();
  const hq = isSuperAdmin(staff);

  // Mặc định tất cả trạng thái. Chưa trả lời vẫn lên đầu danh sách (server sắp xếp).
  const [state, setState] = useState<MealReviewState | 'all'>('all');
  const [branchId, setBranchId] = useState('all');
  const [star, setStar] = useState<number | 'all'>('all');
  const [replying, setReplying] = useState<AdminMealReview | null>(null);
  const [replyText, setReplyText] = useState('');

  const filters: MealReviewFilters = {
    branchId: hq && branchId !== 'all' ? branchId : undefined,
    state: state === 'all' ? undefined : state,
  };

  const branchesQuery = useQuery({
    queryKey: catalogKeys.branches,
    queryFn: catalogApi.listBranches,
    enabled: hq,
  });

  const reviewsQuery = useQuery({
    queryKey: reviewKeys.list(filters),
    queryFn: () => reviewApi.list(filters),
  });

  const reportError = (error: unknown, fallback: string) => {
    message.error(
      error instanceof ApiError || error instanceof NetworkError ? error.message : fallback,
    );
  };

  const replyMutation = useMutation({
    mutationFn: (vars: { id: string; text: string; editing: boolean }) =>
      reviewApi.reply(vars.id, vars.text),
    onSuccess: (_data, vars) => {
      message.success(
        vars.editing
          ? 'Đã cập nhật câu trả lời.'
          : 'Đã gửi trả lời. Khách nhận được thông báo ngay.',
      );
      setReplying(null);
      setReplyText('');
      void queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },
    onError: (error) => reportError(error, 'Không lưu được câu trả lời.'),
  });

  const branchOptions = [
    { value: 'all', label: 'Mọi chi nhánh' },
    ...(branchesQuery.data ?? []).map((b) => ({ value: b.id, label: b.name })),
  ];

  // Lọc sao ở client — BE chỉ nhận lọc theo trạng thái. Với lượng đánh giá của Phase 1 (một hộp
  // vận hành, không phải bảng triệu dòng) một trang 200 dòng là đủ để lọc tại chỗ.
  // Sắp lại ở client để thứ tự giữ nguyên sau khi lọc sao: chưa trả lời lên trước, rồi mới nhất.
  const rows = useMemo(() => {
    const items = reviewsQuery.data?.items ?? [];
    const filtered = star === 'all' ? items : items.filter((r) => r.rating === star);
    return [...filtered].sort((a, b) => {
      const rank = (value: MealReviewState) => (value === 'Replied' ? 1 : 0);
      const byState = rank(a.state) - rank(b.state);
      if (byState !== 0) return byState;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [reviewsQuery.data, star]);

  const unanswered = reviewsQuery.data?.unansweredCount ?? 0;

  const openReply = (review: AdminMealReview) => {
    setReplying(review);
    setReplyText(review.replyText ?? '');
  };

  const columns: ColumnsType<AdminMealReview> = [
    {
      title: 'Thời gian',
      key: 'createdAt',
      width: 150,
      render: (_, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5 }}>
          {formatMoment(row.createdAt)}
        </span>
      ),
    },
    {
      title: 'Sao',
      key: 'rating',
      width: 128,
      render: (_, row) => <Rate disabled value={row.rating} style={{ fontSize: 14 }} />,
    },
    {
      title: 'Khách',
      dataIndex: 'memberCode',
      key: 'memberCode',
      width: 120,
      render: (value: string) => <code style={{ fontSize: 12.5 }}>{value}</code>,
    },
    {
      title: 'Chi nhánh',
      dataIndex: 'branchName',
      key: 'branchName',
      width: 150,
    },
    {
      title: 'Nhận xét',
      dataIndex: 'comment',
      key: 'comment',
      render: (value: string | null) =>
        value ? (
          <span style={{ fontSize: 13 }}>{value}</span>
        ) : (
          <span style={{ color: tokens.color.ink3, fontStyle: 'italic' }}>Không có nhận xét</span>
        ),
    },
    {
      title: 'Điểm tặng',
      key: 'pointsAwarded',
      width: 110,
      align: 'right',
      render: (_, row) =>
        row.pointsAwarded > 0 ? (
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
            {VND.format(row.pointsAwarded)}
          </span>
        ) : (
          <Tag color="default" style={{ margin: 0 }}>
            0
          </Tag>
        ),
    },
    {
      title: 'Trạng thái',
      key: 'state',
      width: 130,
      render: (_, row) => (
        <StatusTag color={STATE_META[row.state].color}>{STATE_META[row.state].label}</StatusTag>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 120,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <Button
          type={row.state === 'Replied' ? 'text' : 'primary'}
          size="small"
          icon={<ChatCircleText size={15} aria-hidden />}
          onClick={() => openReply(row)}
        >
          {row.state === 'Replied' ? 'Sửa' : 'Trả lời'}
        </Button>
      ),
    },
  ];

  // Ba bộ lọc gom vào MỘT nút "Lọc" trong thanh công cụ của bảng (không rải trên PageHeader).
  // Badge đếm số bộ lọc đang thu hẹp; "all" = không thu hẹp.
  const activeFilterCount =
    (state !== 'all' ? 1 : 0) + (star !== 'all' ? 1 : 0) + (hq && branchId !== 'all' ? 1 : 0);

  const resetFilters = () => {
    setState('all');
    setStar('all');
    setBranchId('all');
  };

  const fieldLabel = (text: string) => (
    <span style={{ display: 'block', fontSize: 12, color: tokens.color.ink2, marginBottom: 4 }}>
      {text}
    </span>
  );

  const filterControl = (
    <Popover
      trigger="click"
      placement="bottomRight"
      content={
        <Space direction="vertical" size={12} style={{ width: 240 }}>
          <div>
            {fieldLabel('Trạng thái')}
            <Select
              value={state}
              onChange={setState}
              style={{ width: '100%' }}
              options={[
                { value: 'all', label: 'Tất cả trạng thái' },
                { value: 'New', label: 'Mới' },
                { value: 'Seen', label: 'Đã xem' },
                { value: 'Replied', label: 'Đã trả lời' },
              ]}
            />
          </div>
          <div>
            {fieldLabel('Số sao')}
            <Select
              value={star}
              onChange={setStar}
              style={{ width: '100%' }}
              options={[
                { value: 'all', label: 'Mọi số sao' },
                { value: 5, label: '5 sao' },
                { value: 4, label: '4 sao' },
                { value: 3, label: '3 sao' },
                { value: 2, label: '2 sao' },
                { value: 1, label: '1 sao' },
              ]}
            />
          </div>
          {hq && (
            <div>
              {fieldLabel('Chi nhánh')}
              <Select
                value={branchId}
                onChange={setBranchId}
                style={{ width: '100%' }}
                options={branchOptions}
              />
            </div>
          )}
          <Button
            type="link"
            size="small"
            onClick={resetFilters}
            disabled={activeFilterCount === 0}
            style={{ padding: 0, alignSelf: 'flex-start' }}
          >
            Đặt lại bộ lọc
          </Button>
        </Space>
      }
    >
      <Badge count={activeFilterCount} size="small" offset={[-4, 2]}>
        <Button icon={<FunnelSimple size={15} aria-hidden />} size="small">
          Lọc
        </Button>
      </Badge>
    </Popover>
  );

  return (
    <>
      <PageHeader
        title="Đánh giá bữa ăn"
        subtitle={
          unanswered > 0
            ? `Còn ${VND.format(unanswered)} đánh giá chưa trả lời`
            : 'Đã trả lời hết đánh giá'
        }
      />
      <PageBody>
        {reviewsQuery.isError ? (
          <ErrorState error={reviewsQuery.error} onRetry={() => void reviewsQuery.refetch()} />
        ) : (
          <>
            <Card styles={{ body: { paddingTop: 8 } }} style={{ borderRadius: tokens.radius.card }}>
              <DataTable
                rowKey="id"
                title="Hộp đánh giá"
                subTitle={
                  star === 'all'
                    ? `${rows.length} đánh giá`
                    : `${rows.length} đánh giá ${star} sao (đã lọc)`
                }
                onReload={() => void reviewsQuery.refetch()}
                searchPlaceholder="Tìm theo khách, nhận xét"
                toolbarExtra={filterControl}
                loading={reviewsQuery.isFetching}
                columns={columns}
                dataSource={rows}
                scroll={{ x: 1040 }}
                locale={{ emptyText: 'Không có đánh giá nào khớp bộ lọc.' }}
              />
            </Card>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12.5, marginTop: 16 }}>
              Ô "Điểm tặng" bằng 0 khi đánh giá không gắn được hóa đơn hoặc chương trình tặng điểm đang
              tắt — đánh giá vẫn luôn được nhận. Trả lời xong khách nhận thông báo ngay.
              {hq ? '' : ' Bạn chỉ thấy đánh giá của chi nhánh mình.'}
            </Typography.Paragraph>
          </>
        )}
      </PageBody>

      <Modal
        title={replying?.state === 'Replied' ? 'Sửa câu trả lời' : 'Trả lời đánh giá'}
        open={replying !== null}
        onCancel={() => setReplying(null)}
        okText={replying?.state === 'Replied' ? 'Sửa câu trả lời' : 'Gửi trả lời'}
        okButtonProps={{
          icon:
            replying?.state === 'Replied' ? (
              <PencilSimple size={15} aria-hidden />
            ) : (
              <PaperPlaneRight size={15} aria-hidden />
            ),
          loading: replyMutation.isPending,
          disabled: replyText.trim().length === 0,
        }}
        onOk={() => {
          if (replying && replyText.trim().length > 0) {
            replyMutation.mutate({
              id: replying.id,
              text: replyText.trim(),
              editing: replying.state === 'Replied',
            });
          }
        }}
        destroyOnClose
      >
        {replying && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <div
              style={{
                background: tokens.color.surface2,
                border: `1px solid ${tokens.color.line}`,
                borderRadius: tokens.radius.control,
                padding: 12,
              }}
            >
              <Space size={8} style={{ marginBottom: 4 }}>
                <Rate disabled value={replying.rating} style={{ fontSize: 14 }} />
                <code style={{ fontSize: 12 }}>{replying.memberCode}</code>
                <span style={{ fontSize: 12, color: tokens.color.ink3 }}>· {replying.branchName}</span>
              </Space>
              <div style={{ fontSize: 13 }}>
                {replying.comment ?? (
                  <span style={{ color: tokens.color.ink3, fontStyle: 'italic' }}>
                    Khách không để lại nhận xét
                  </span>
                )}
              </div>
            </div>

            {replying.state === 'Replied' && (
              <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
                Đánh giá này đã trả lời — sửa xong sẽ cập nhật câu trả lời (khách không nhận
                thông báo lần nữa).
              </Typography.Text>
            )}

            {/* showCount đặt số ký tự absolute bên dưới ô. Không chừa chỗ thì nó đè nút ở chân modal. */}
            <div style={{ paddingBottom: 22 }}>
              <Input.TextArea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Câu trả lời gửi tới khách…"
                autoSize={{ minRows: 3, maxRows: 8 }}
                maxLength={REPLY_MAX}
                showCount
                aria-label="Nội dung trả lời"
              />
            </div>
          </Space>
        )}
      </Modal>
    </>
  );
}
